/**
 * @fileoverview Main TypeScript file for managing the onboarding process.
 * This file defines the onboarding state and methods used to handle step transitions,
 * UI state updates, and redirection during onboarding.
 */

/* ================================
   Interfaces & Types
================================ */

/**
 * Represents a project (or codebase) with a unique ID and name.
 */
interface Project {
    id: number;
    name: string;
    uuid?: string;
}

/**
 * Interface for the onboarding data object.
 */
interface OnboardingData {
    step: string;
    completed: boolean;
    activeModal: string | null;
    loading: boolean;
    copied: string;
    hasExistingCodebases: boolean;
    projects: Project[];
    installComplete: boolean;
    email: string;
    redirectCountdown: number;
    redirectStarted: boolean;
    scanComplete: boolean;
    cliLastAuth: string;
    github_api_key: string;

    // Utility methods
    formatDate(dateString: string): string;
    getStep(): number;

    // UI state update methods
    setStep(step: string | number): Promise<void>;
    setActiveModal(modal: string): void;
    closeModal(): void;
    copyCommand(command: string): void;
    nextStep(): Promise<void>;
    restart(): Promise<void>;
    skipOnboarding(): Promise<void>;

    // Step-specific methods
    initStep3(): Promise<void>;
    markInstallComplete(): void;

    // Finalization / Redirection methods
    finalizeOnboarding(): Promise<void>;
    startRedirectCountdown(): void;

    // Initialization
    init(): void;
}

/* ================================
   Constants
================================ */

/**
 * Mapping between step names and step numbers.
 */
const stepMap: { [key: string]: number } = {
    'learn': 1,
    'install': 2,
    'secure': 3,
};

/* ================================
   Onboarding State Factory
================================ */

/**
 * Creates and returns the onboarding data, initializing state
 * from the server-provided data present in the DOM.
 *
 * @returns {OnboardingData} The onboarding state and its methods.
 */
export default (): OnboardingData => {
    // Get initial data from the Django template (this JSON comes from the /get-started response)
    const onboardingData = JSON.parse(
        document.getElementById('onboarding-data')?.textContent || '{}'
    );

    return {
        /* ---------------------------
           State Properties
        ---------------------------- */
        step: onboardingData.current_step || 'learn',
        completed: onboardingData.completed || false,
        github_api_key: onboardingData.github_api_key || '',
        activeModal: null as string | null,
        loading: true,
        copied: '',
        hasExistingCodebases: onboardingData.has_codebases || onboardingData.has_projects || false,
        projects: onboardingData.projects || [],
        installComplete: false,
        email: onboardingData.email || '',
        redirectCountdown: 0,
        redirectStarted: false,
        scanComplete: false,
        cliLastAuth: onboardingData.cliLastAuth || '',

        /* ---------------------------
           Utility Methods
        ---------------------------- */
        /**
         * Formats a given date string into a human-friendly relative date.
         *
         * @param {string} dateString - The date string to format.
         * @returns {string} A human-friendly date representation.
         */
        formatDate(dateString: string): string {
            if (!dateString) {
                return '';
            }
            const date = new Date(dateString);
            const now = new Date();

            // Get the start (midnight) of today.
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            // Get the start (midnight) of the authentication date.
            const authDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

            // Calculate the difference in whole days.
            const diffInTime = today.getTime() - authDate.getTime();
            const diffInDays = Math.floor(diffInTime / (1000 * 60 * 60 * 24));

            if (diffInDays === 0) {
                return "Today at " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else if (diffInDays === 1) {
                return "Yesterday at " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else {
                return `${diffInDays} days ago`;
            }
        },

        /**
         * Retrieves the current numeric step based on the stepMap.
         *
         * @returns {number} The current step number.
         */
        getStep(): number {
            return stepMap[this.step] || 1;
        },

        /* ---------------------------
           UI State Update Methods
        ---------------------------- */
        /**
         * Sets the current onboarding step and notifies the backend.
         *
         * @param {string | number} step - The new step (either a step name or a number).
         */
        async setStep(step: string | number): Promise<void> {
            // Store the previous step before making any changes
            const previousStep = this.step;

            try {
                // Optimistically update UI immediately.
                let newStep: string;
                if (typeof step === 'number') {
                    const reverseMap = Object.entries(stepMap).find(([_, val]) => val === step);
                    newStep = reverseMap ? reverseMap[0] : String(step);
                } else {
                    newStep = String(step);
                }
                this.step = newStep;

                // If moving to the secure step, load additional data in the background.
                if (newStep === 'secure') {
                    await this.initStep3();
                }

                // Turn off the loading state immediately.
                this.loading = false;

                // Post the update to the backend asynchronously.
                const csrfToken = document.cookie
                    .split('; ')
                    .find(row => row.startsWith('csrftoken='))
                    ?.split('=')[1];

                const response = await fetch('/get-started/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken || '',
                    },
                    body: JSON.stringify({ step: newStep })
                });

                if (!response.ok) {
                    throw new Error('Failed to update step');
                }
            } catch (error) {
                // Check if the error is from a cancelled transition
                if (error && typeof error === 'object' && 'isFromCancelledTransition' in error) {
                    // Silently ignore cancelled transition errors
                    return;
                }
                console.error('Error updating step:', error);
                // Revert the optimistic UI update
                this.step = previousStep;
            }
        },

        /**
         * Sets the active modal in the UI.
         *
         * @param {string} modal - The modal identifier to set as active.
         */
        setActiveModal(modal: string): void {
            this.activeModal = modal;
        },

        /**
         * Closes any active modal.
         */
        closeModal(): void {
            this.activeModal = null;
        },

        /**
         * Copies a given command to the clipboard.
         * Sets the 'copied' state based on the content type.
         *
         * @param {string} command - The command to copy.
         */
        async copyCommand(command: string): Promise<void> {
            await navigator.clipboard.writeText(command);
            // Determine which command was copied.
            this.copied = command.includes('safety scan') ? 'scan' : 'install';
            // Reset copied state after 2 seconds.
            setTimeout(() => {
                this.copied = '';
            }, 2000);
        },

        /**
         * Advances the onboarding to the next step.
         */
        async nextStep(): Promise<void> {
            const currentStep = this.getStep();
            const nextStepMap: { [key: number]: string } = {
                1: 'install',
                2: 'secure'
            };

            if (currentStep === 3) {
                console.log("Secure step reached. Please use finalizeOnboarding() button.");
                return;
            }

            await this.setStep(nextStepMap[currentStep] || 'learn');
        },

        /**
         * Resets the onboarding process to its initial state.
         */
        async restart(): Promise<void> {
            try {
                const csrfToken = document.cookie
                    .split('; ')
                    .find(row => row.startsWith('csrftoken='))
                    ?.split('=')[1];

                const response = await fetch('/get-started/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken || '',
                    },
                    body: JSON.stringify({ action: 'reset' })
                });

                if (!response.ok) {
                    console.error('Failed to reset onboarding');
                }

                // Reset local UI state.
                this.step = 'learn';
                this.completed = false;
                this.activeModal = null;
                this.installComplete = false;
                this.redirectCountdown = 0;
                this.redirectStarted = false;
                this.scanComplete = false;

            } catch (error) {
                console.error('Error during restart:', error);
                this.step = 'learn';
                this.completed = false;
                this.activeModal = null;
                this.installComplete = false;
                this.redirectCountdown = 0;
                this.redirectStarted = false;
                this.scanComplete = false;
            }
        },

        async skipOnboarding(): Promise<void> {
            // Retrieve CSRF token from cookies
            const csrfToken = document.cookie
                .split('; ')
                .find(row => row.startsWith('csrftoken='))
                ?.split('=')[1];
            try {
                const response = await fetch('/get-started/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken || '',
                    },
                    body: JSON.stringify({ action: 'skip' })
                });
                if (!response.ok) {
                    console.error('Failed to skip onboarding');
                } else {
                    console.log("Onboarding skipped successfully.");
                    // Mark onboarding as complete locally and redirect (if desired)
                    this.completed = true;
                    window.location.href = '/';
                }
            } catch (error) {
                console.error('Error while skipping onboarding:', error);
            }
        },

        /* ---------------------------
           Step-Specific Methods
        ---------------------------- */
        /**
         * Initializes additional data for the secure (step 3) onboarding step.
         */
        async initStep3(): Promise<void> {
            if (this.getStep() === 3) {
                // Use already loaded data from the onboarding context.
                this.hasExistingCodebases = this.projects.length > 0;
            }
        },

        /**
         * Marks the installation step as complete.
         */
        markInstallComplete(): void {
            this.installComplete = true;
        },

        /* ---------------------------
           Finalization & Redirection
        ---------------------------- */
        /**
         * Finalizes the onboarding process by marking it as complete with the backend.
         */
        async finalizeOnboarding(): Promise<void> {
            // First mark onboarding as complete before any redirects
            try {
                const csrfToken = document.cookie
                    .split('; ')
                    .find(row => row.startsWith('csrftoken='))
                    ?.split('=')[1];

                const response = await fetch('/get-started/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken || '',
                    },
                    body: JSON.stringify({ step: 'secure', completed: true })
                });

                if (!response.ok) {
                    throw new Error('Failed to mark onboarding as completed');
                }

                this.completed = true;
                console.log("Onboarding marked as complete.");

                // Get the latest projects data before deciding redirect
                const projectsResponse = await fetch('/api/v1/codebases/');
                if (!projectsResponse.ok) {
                    throw new Error('Failed to fetch codebases data');
                }


                window.location.href = '/';

            } catch (error) {
                console.error('Error during onboarding finalization:', error);
                // On error, stay on current page instead of redirecting
                this.completed = false;
            }
        },

        /**
         * Starts the redirect countdown timer.
         * Once the countdown reaches zero, proceeds to the next step or finalizes onboarding.
         */
        startRedirectCountdown(): void {
            this.redirectStarted = true;
            this.redirectCountdown = 10;
            const timer = setInterval(() => {
                this.redirectCountdown -= 1;
                if (this.redirectCountdown <= 0) {
                    clearInterval(timer);
                    this.redirectStarted = false;
                    if (this.getStep() < 3) {
                        // If not yet on secure (step 3), move to the next step.
                        this.nextStep().catch(err => console.error(err));
                    } else {
                        // If on secure step, finalize onboarding.
                        this.finalizeOnboarding();
                    }
                }
            }, 1000);
        },

        /* ---------------------------
           Initialization
        ---------------------------- */
        /**
         * Initializes event listeners for onboarding events.
         * Listens for events such as installation completion and scan creation.
         */
        init(): void {
            window.addEventListener('onboarding:install-complete', () => {
                this.markInstallComplete();
                // Wait until the user's window is focused before starting the countdown.
                if (document.hasFocus()) {
                    this.startRedirectCountdown();
                } else {
                    window.addEventListener('focus', () => {
                        this.startRedirectCountdown();
                    }, { once: true });
                }
            });
            window.addEventListener('onboarding:scan-created', () => {
                // Only update on secure step if scan isn't already complete.
                if (this.getStep() === 3 && !this.scanComplete) {
                    this.scanComplete = true;
                    // Start the redirect countdown only if there are no existing codebases
                    if (!this.redirectStarted && !this.hasExistingCodebases) {
                        this.startRedirectCountdown();
                    }
                }
            });
        }
    }
}
